import React, { useEffect, useMemo, useState } from "react";
import commonStyles from "../../common/CommonStyles.module.css";
import s from "./Contests.module.css";

const AdminContests = () => {
  const [contests, setContests] = useState([]);
  const [editingContest, setEditingContest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await fetch("/api/admin/contests");
      if (!response.ok) throw new Error("Ошибка при загрузке конкурсов");
      const data = await response.json();
      setContests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching contests:", error);
      setErrorMessage("Ошибка при загрузке конкурсов");
      setContests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Вы уверены, что хотите удалить конкурс?")) return;
    setErrorMessage("");
    try {
      const response = await fetch(`/api/admin/contests/${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Ошибка при удалении конкурса");
      fetchContests();
    } catch (error) {
      console.error("Error deleting contest:", error);
      setErrorMessage("Ошибка при удалении конкурса");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const formData = new FormData(event.target);
    const name = String(formData.get("name") || "").trim();
    const payload = {
      name: name || null,
      url: String(formData.get("url") || "").trim(),
      description: String(formData.get("description") || "").trim(),
    };

    if (!payload.url || !payload.description) {
      setErrorMessage("Заполните ссылку и описание");
      return;
    }

    const url = editingContest ? `/api/admin/contests/${editingContest.id}` : "/api/admin/contests";
    const method = editingContest ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Ошибка при сохранении конкурса");
      setIsModalOpen(false);
      setEditingContest(null);
      fetchContests();
    } catch (error) {
      console.error("Error saving contest:", error);
      setErrorMessage("Ошибка при сохранении конкурса");
    }
  };

  const sortedContests = useMemo(() => {
    return [...contests].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [contests]);

  return (
    <div className={commonStyles.pageContainer}>
      <div className={commonStyles.contentContainer}>
        <div className={commonStyles.content}>
          <h2 className={s.title}>Управление конкурсами</h2>

          <div className={s.actionsTop}>
            <button
              className={s.addButton}
              onClick={() => {
                setIsModalOpen(true);
                setEditingContest(null);
              }}
            >
              <span className={s.plus}>+</span> Добавить конкурс
            </button>
          </div>

          {isLoading ? (
            <div className={s.centerMessage}>Загрузка...</div>
          ) : (
            <div className={s.tableContainer}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Ссылка</th>
                    <th>Описание</th>
                    <th>Добавлено</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedContests.map((contest) => (
                    <tr key={contest.id} className={s.tableRow}>
                      <td className={s.nameCell}>{contest.name}</td>
                      <td className={s.urlCell}>
                        <a href={contest.url} target="_blank" rel="noreferrer" className={s.urlLink}>
                          {contest.url}
                        </a>
                      </td>
                      <td className={s.descCell} title={contest.description}>
                        {contest.description}
                      </td>
                      <td className={s.dateCell}>
                        {contest.createdAt ? new Date(contest.createdAt).toLocaleDateString("ru-RU") : "—"}
                      </td>
                      <td className={s.actionsCell}>
                        <button
                          className={s.editButton}
                          onClick={() => {
                            setEditingContest(contest);
                            setIsModalOpen(true);
                          }}
                        >
                          Редактировать
                        </button>
                        <button className={s.deleteButton} onClick={() => handleDelete(contest.id)}>
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!sortedContests.length && (
                    <tr>
                      <td colSpan={5} className={s.emptyRow}>
                        Пока нет конкурсов
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {errorMessage && <div className={s.errorBox}>{errorMessage}</div>}

          {isModalOpen && (
            <div className={s.modal}>
              <div className={s.modalContent}>
                <h3 className={s.modalTitle}>{editingContest ? "Редактирование" : "Новый конкурс"}</h3>
                <form onSubmit={handleSubmit}>
                  <div className={s.formSection}>
                    <label className={s.label}>Название:</label>
                    <input
                      name="name"
                      defaultValue={editingContest?.name ?? ""}
                      className={s.input}
                      placeholder="Название конкурса"
                    />
                  </div>

                  <div className={s.formSection}>
                    <label className={s.label}>Ссылка:</label>
                    <input
                      name="url"
                      defaultValue={editingContest?.url}
                      required
                      className={s.input}
                      placeholder="https://..."
                    />
                  </div>

                  <div className={s.formSection}>
                    <label className={s.label}>Описание:</label>
                    <textarea
                      name="description"
                      defaultValue={editingContest?.description}
                      required
                      className={s.textarea}
                      placeholder="Опишите конкурс"
                    />
                  </div>

                  <div className={s.modalActions}>
                    <button type="submit" className={s.saveButton}>
                      Сохранить
                    </button>
                    <button
                      type="button"
                      className={s.cancelButton}
                      onClick={() => {
                        setIsModalOpen(false);
                        setEditingContest(null);
                        setErrorMessage("");
                      }}
                    >
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContests;
